import { clamp, hexToRgb, luminance, mixRgb, type Rgb } from '../../engine/math';
import { keyBlendOf } from '../../engine/keyedBackdrop';
import type { ChromeTone } from '../../engine/types';

// Three key lights on the river: a misty morning in the woods, bright midday
// over the meadows, and the golden afternoon on the lake.
const STOPS = {
  skyTop: ['#9CC4E0', '#7DB8EA', '#8FB4DC'],
  skyMiddle: ['#CFE2EA', '#B9DCF2', '#F4D2A8'],
  skyHorizon: ['#EEF0E4', '#E6F2F2', '#FFD9A0'],
  sun: ['#FFF6E0', '#FFFFF4', '#FFE2A0'],
  cloud: ['#FFFFFF', '#FFFFFF', '#FFF0D8'],
  cloudShade: ['#C6D4DE', '#C2D6E8', '#E8B8A0'],
  mountain: ['#9EB4C6', '#93B0CC', '#B49CB0'],
  hillFar: ['#88A898', '#7EAA88', '#A0A070'],
  hill: ['#5E8A66', '#5A9460', '#7E8A50'],
  ground: ['#7FA45E', '#8CBA5C', '#A6A85A'],
  groundNear: ['#577E44', '#5E9244', '#6E7C3A'],
  water: ['#8FB6C2', '#7FBCD6', '#B8B6A4'],
  waterDeep: ['#3E6A78', '#2E6E8C', '#4E5E6E'],
  waterGlow: ['#E6F0EC', '#F4FBFF', '#FFE2A8'],
  bank: ['#6E5E44', '#76623E', '#7A5A36'],
  haze: ['#DCE6E2', '#DDECEF', '#F6D8B0'],
  light: ['#E0ECF4', '#FFFFFF', '#FFC878'],
} as const;

type ColorName = keyof typeof STOPS;
export type RiverColors = Record<ColorName, Rgb>;

export const RIVER_KEYS = 3;

const NAMES = Object.keys(STOPS) as ColorName[];
const PARSED = {} as Record<ColorName, Rgb[]>;
NAMES.forEach((name) => {
  PARSED[name] = STOPS[name].map(hexToRgb);
});

export const riverKeyColors = (index: number): RiverColors => {
  const key = Math.min(RIVER_KEYS - 1, Math.max(0, Math.round(index) || 0));
  const colors = {} as RiverColors;
  NAMES.forEach((name) => {
    colors[name] = PARSED[name][key];
  });
  return colors;
};

/** Morning mist to midday with the journey; the lake opens in the golden afternoon. */
export const riverLight = (progress: number, finale: number) =>
  clamp(clamp(progress) * 0.55 + clamp(finale) * 0.45);

export const riverColors = (light: number): RiverColors => {
  const { index, amount } = keyBlendOf(light, RIVER_KEYS);
  const colors = {} as RiverColors;
  NAMES.forEach((name) => {
    colors[name] = mixRgb(PARSED[name][index], PARSED[name][index + 1], amount);
  });
  return colors;
};

/** A light sky over the whole journey keeps the header text dark. */
export const riverChromeTone = (progress: number): ChromeTone =>
  luminance(riverColors(riverLight(progress, progress >= 0.995 ? 1 : 0)).skyTop) > 0.3 ? 'dark' : 'light';
